import { Card, CardContent, CardHeader } from "@workspace/ui/components/card";
import { Skeleton } from "@workspace/ui/components/skeleton";

export function ShowDetailSkeleton() {
	return (
		<div className="container mx-auto max-w-3xl px-4 py-3">
			<div className="flex flex-col gap-6">
				{/* Navigation and Show Header skeleton */}
				<div>
					{/* Navigation skeleton */}
					<div className="flex items-center justify-between gap-2">
						<div className="flex items-center gap-2">
							<Skeleton className="h-8 w-16" />
							<div className="hidden h-4 w-px bg-muted sm:block" />
							<Skeleton className="hidden h-5 w-48 sm:block" />
						</div>
						<Skeleton className="h-8 w-24" />
					</div>

					{/* Show Detail Card Skeleton */}
					<Card className="mt-6 h-full">
						<CardHeader className="h-full p-3">
							<div className="flex flex-col sm:flex-row sm:gap-4">
								{/* Show poster skeleton */}
								<div className="relative mx-auto hidden w-full flex-shrink-0 sm:mx-0 sm:block sm:w-32 md:w-40 lg:w-48">
									<div className="aspect-square overflow-hidden rounded-lg">
										<Skeleton className="h-full w-full" />
									</div>
								</div>

								{/* Show info skeleton */}
								<div className="flex-1">
									{/* Title */}
									<Skeleton className="mb-3 h-6 w-3/4" />

									<div className="space-y-3">
										{/* Venue */}
										<div className="flex items-center gap-2">
											<Skeleton className="h-4 w-4 flex-shrink-0" />
											<Skeleton className="h-4 w-32" />
										</div>

										{/* Location */}
										<div className="flex items-center gap-2">
											<Skeleton className="h-4 w-4 flex-shrink-0" />
											<Skeleton className="h-4 w-48" />
										</div>

										{/* Date */}
										<div className="flex items-center gap-2">
											<Skeleton className="h-4 w-4 flex-shrink-0" />
											<Skeleton className="h-4 w-40" />
										</div>

										{/* Time */}
										<div className="flex items-center gap-2">
											<Skeleton className="h-4 w-4 flex-shrink-0" />
											<Skeleton className="h-4 w-36" />
										</div>
									</div>
								</div>
							</div>
						</CardHeader>
					</Card>
				</div>

				{/* Artist Cards Section skeleton */}
				<div className="space-y-3">
					{/* Headliner Skeleton - larger card */}
					<Card>
						<CardHeader className="p-3">
							<div className="flex gap-3">
								{/* Larger artist photo */}
								<Skeleton className="h-20 w-20 flex-shrink-0 rounded-card" />

								{/* Artist info */}
								<div className="min-w-0 flex-1">
									<div className="flex items-start justify-between gap-2">
										<div className="flex-1">
											<Skeleton className="mb-1 h-6 w-32" />
											<Skeleton className="h-3 w-24" />
										</div>
										{/* Social buttons */}
										<div className="flex gap-1">
											<Skeleton className="h-8 w-8 rounded-md" />
											<Skeleton className="h-8 w-8 rounded-md" />
											<Skeleton className="h-8 w-8 rounded-md" />
										</div>
									</div>
								</div>
							</div>
						</CardHeader>
						<CardContent className="px-3 pt-0 pb-3">
							{/* Genres section */}
							<div className="grid grid-cols-2 gap-4">
								<div>
									<Skeleton className="mb-1 h-4 w-12" />
									<div className="flex gap-1">
										<Skeleton className="h-5 w-14 rounded-full" />
										<Skeleton className="h-5 w-16 rounded-full" />
										<Skeleton className="h-5 w-12 rounded-full" />
									</div>
								</div>
								<div>
									<Skeleton className="mb-1 h-4 w-24" />
									<div className="flex -space-x-2">
										<Skeleton className="h-6 w-6 rounded-full" />
										<Skeleton className="h-6 w-6 rounded-full" />
										<Skeleton className="h-6 w-6 rounded-full" />
									</div>
								</div>
							</div>
						</CardContent>
					</Card>

					{/* Spotify Embed Card skeleton */}
					<Card>
						<CardContent className="p-3">
							<Skeleton className="h-[152px] w-full rounded-xl" />
						</CardContent>
					</Card>

					{/* Supporting Artists Skeleton */}
					{[1, 2].map((id) => (
						<Card key={`supporting-${id}`}>
							<CardContent className="flex items-center gap-3 p-3">
								{/* Artist image */}
								<Skeleton className="h-14 w-14 flex-shrink-0 rounded-card" />

								{/* Artist info */}
								<div className="min-w-0 flex-1">
									<div className="flex items-center justify-between gap-2">
										<div className="flex-1">
											<Skeleton className="mb-1 h-5 w-32" />
											<Skeleton className="h-3 w-24" />
										</div>
										{/* Social buttons */}
										<div className="flex gap-1">
											<Skeleton className="h-7 w-7 rounded-md" />
											<Skeleton className="h-7 w-7 rounded-md" />
										</div>
									</div>
								</div>
							</CardContent>
						</Card>
					))}
				</div>

				{/* Your Review Section Skeleton */}
				<div className="space-y-4">
					<Skeleton className="h-6 w-28" />
					<Card className="p-3 text-center">
						<div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
							<Skeleton className="h-6 w-6" />
						</div>
						<Skeleton className="mx-auto mb-1 h-6 w-40" />
						<Skeleton className="mx-auto h-4 w-56" />
					</Card>
				</div>

				{/* Community Reviews Section Skeleton */}
				<div className="space-y-4">
					<Skeleton className="h-6 w-40" />
					<div className="space-y-3">
						{[1, 2, 3].map((id) => (
							<Card key={`review-${id}`}>
								<CardContent className="p-3">
									<div className="flex gap-3">
										{/* Profile pic */}
										<Skeleton className="h-12 w-12 flex-shrink-0 rounded-full" />

										{/* Review content */}
										<div className="flex-1">
											<Skeleton className="mb-1 h-4 w-24" />
											<div className="mb-2 flex items-center gap-1">
												<Skeleton className="h-5 w-20" />
											</div>
											<Skeleton className="h-4 w-full" />
											<Skeleton className="mt-1 h-4 w-3/4" />
										</div>

										{/* Status badges */}
										<div className="flex flex-col gap-1">
											<Skeleton className="h-6 w-16 rounded-lg" />
										</div>
									</div>
								</CardContent>
							</Card>
						))}
					</div>
				</div>
			</div>
		</div>
	);
}
